/**
 * AudioCache - Manages a sliding window cache for karaoke audio tracks
 * Keeps previous, current, and next songs loaded for instant transitions
 */

import {
  getSongMediaKey,
  getVocalTrackUrl,
  getAccompanimentTrackUrl,
  getDrumsTrackUrl,
  getMainAudioUrl,
  hasAnyPlayableMedia,
} from '../lib/songMedia'

class AudioCache {
  constructor(maxCacheSize = 3) {
    // Map of mediaKey -> { vocal, accompaniment, drums, loadTime, accessTime }
    this.cache = new Map()
    this.maxCacheSize = maxCacheSize
    this.loadingPromises = new Map() // Track ongoing loads to prevent duplicates
    
    // Callbacks for cache events
    this.onCacheHit = null
    this.onCacheMiss = null
    this.onCacheInvalidation = null
  }

  /**
   * Get cached audio buffers for a song
   * @param {string} mediaKey - Canonical song media key
   * @returns {Object|null} - { vocal, accompaniment, drums } or null if not cached
   */
  get(mediaKey) {
    if (!mediaKey) return null
    
    const cacheEntry = this.cache.get(mediaKey)
    if (cacheEntry) {
      // Update access time for LRU
      cacheEntry.accessTime = Date.now()
      console.log(`🎵 📦 CACHE HIT: ${mediaKey}`)
      if (this.onCacheHit) this.onCacheHit(mediaKey)
      return {
        vocal: cacheEntry.vocal,
        accompaniment: cacheEntry.accompaniment,
        drums: cacheEntry.drums
      }
    }
    
    console.log(`🎵 📦 CACHE MISS: ${mediaKey}`)
    if (this.onCacheMiss) this.onCacheMiss(mediaKey)
    return null
  }

  /**
   * Check if a song is cached
   * @param {string} mediaKey - Canonical song media key
   * @returns {boolean}
   */
  has(mediaKey) {
    return this.cache.has(mediaKey)
  }

  /**
   * Store audio buffers in cache
   * @param {string} mediaKey - Canonical song media key
   * @param {Object} buffers - { vocal, accompaniment, drums } AudioBuffer objects
   */
  set(mediaKey, buffers) {
    if (!mediaKey) return

    const now = Date.now()
    this.cache.set(mediaKey, {
      vocal: buffers.vocal,
      accompaniment: buffers.accompaniment,
      drums: buffers.drums,
      loadTime: now,
      accessTime: now
    })

    console.log(`🎵 📦 CACHED: ${mediaKey} (${this.cache.size}/${this.maxCacheSize})`)
    
    // Enforce cache size limit using LRU eviction
    this.enforceMaxSize()
  }

  /**
   * Load and cache audio buffers for a song
   * @param {Object} song - Normalized song object with canonical media URLs
   * @param {AudioContext} audioContext - Web Audio API context
   * @returns {Promise<Object>} - Promise resolving to { vocal, accompaniment, drums }
   */
  async loadAndCache(song, audioContext) {
    const mediaKey = getSongMediaKey(song)
    if (!song || !mediaKey) {
      throw new Error('Invalid song object')
    }
    
    // Check if already cached
    const cached = this.get(mediaKey)
    if (cached) {
      return cached
    }

    // Check if already loading to prevent duplicate requests
    if (this.loadingPromises.has(mediaKey)) {
      console.log(`🎵 📦 LOADING IN PROGRESS: ${mediaKey} - waiting...`)
      return await this.loadingPromises.get(mediaKey)
    }

    // Start loading
    console.log(`🎵 📦 LOADING: ${mediaKey}`)
    const loadPromise = this.loadAudioBuffers(song, audioContext)
    this.loadingPromises.set(mediaKey, loadPromise)

    try {
      const buffers = await loadPromise
      this.set(mediaKey, buffers)
      return buffers
    } finally {
      // Clean up loading promise
      this.loadingPromises.delete(mediaKey)
    }
  }

  /**
   * Load audio buffers from URLs
   * @private
   */
  async loadAudioBuffers(song, audioContext) {
    const mediaKey = getSongMediaKey(song)
    const vocalUrl = getVocalTrackUrl(song)
    const accompanimentUrl = getAccompanimentTrackUrl(song)
    const drumsUrl = getDrumsTrackUrl(song)
    const mainAudioUrl = getMainAudioUrl(song)

    const buffers = {}

    try {
      // Try to load separated tracks first
      const loadPromises = []
      
      // Vocal track
      if (vocalUrl) {
        loadPromises.push(
          this.fetchAndDecodeAudio(vocalUrl, audioContext)
            .then(buffer => { buffers.vocal = buffer })
            .catch((error) => { 
              console.warn(`🎵 📦 Failed to load vocal track: ${error.message}`);
              buffers.vocal = null 
            })
        )
      }

      // Accompaniment track
      if (accompanimentUrl) {
        loadPromises.push(
          this.fetchAndDecodeAudio(accompanimentUrl, audioContext)
            .then(buffer => { buffers.accompaniment = buffer })
            .catch((error) => { 
              console.warn(`🎵 📦 Failed to load accompaniment track: ${error.message}`);
              buffers.accompaniment = null 
            })
        )
      }

      // Drums track (optional)
      if (drumsUrl) {
        loadPromises.push(
          this.fetchAndDecodeAudio(drumsUrl, audioContext)
            .then(buffer => { buffers.drums = buffer })
            .catch((error) => { 
              console.warn(`🎵 📦 Failed to load drums track: ${error.message}`);
              buffers.drums = null 
            })
        )
      }

      await Promise.all(loadPromises)

      // If no separated tracks, try main audio as fallback
      if (!buffers.vocal && !buffers.accompaniment && mainAudioUrl) {
        console.log(`🎵 📦 Fallback to main audio for ${mediaKey}`)
        try {
          buffers.vocal = await this.fetchAndDecodeAudio(mainAudioUrl, audioContext)
          buffers.accompaniment = null // No accompaniment in main audio
        } catch (error) {
          console.warn(`🎵 📦 Failed to load main audio for ${mediaKey}: ${error.message}`)
          // Don't throw here - we'll handle this below
        }
      }

      // If still no audio tracks available, this song can't be played
      if (!buffers.vocal && !buffers.accompaniment) {
        console.warn(`🎵 📦 No audio files available for ${song.title} (${mediaKey})`)
        throw new Error(`Audio files not found for song: ${song.title}. The song may need to be re-processed.`)
      }

      return buffers
    } catch (error) {
      // More specific error message
      const songInfo = song.title ? `${song.title} (${mediaKey})` : mediaKey
      console.error(`🎵 📦 Failed to load audio buffers for ${songInfo}:`, error)
      
      // If this is a missing files error, don't cache it
      if (error.message.includes('Audio files not found')) {
        throw error
      }
      
      // For other errors, still throw but with more context
      throw new Error(`Failed to load audio for ${songInfo}: ${error.message}`)
    }
  }

  /**
   * Fetch and decode audio from URL
   * @private
   */
  async fetchAndDecodeAudio(url, audioContext) {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    return await audioContext.decodeAudioData(arrayBuffer)
  }

  /**
   * Remove a specific song from cache
   * @param {string} mediaKey - Canonical song media key to remove
   */
  invalidate(mediaKey) {
    if (this.cache.has(mediaKey)) {
      this.cache.delete(mediaKey)
      console.log(`🎵 📦 INVALIDATED: ${mediaKey}`)
      if (this.onCacheInvalidation) this.onCacheInvalidation(mediaKey)
    }
    
    // Also cancel any ongoing load
    if (this.loadingPromises.has(mediaKey)) {
      this.loadingPromises.delete(mediaKey)
      console.log(`🎵 📦 CANCELLED LOADING: ${mediaKey}`)
    }
  }

  /**
   * Clear all cached songs
   */
  clear() {
    const clearedCount = this.cache.size
    this.cache.clear()
    this.loadingPromises.clear()
    console.log(`🎵 📦 CLEARED CACHE: ${clearedCount} songs removed`)
  }

  /**
   * Enforce maximum cache size using LRU eviction
   * @private
   */
  enforceMaxSize() {
    if (this.cache.size <= this.maxCacheSize) return

    // Find least recently accessed entry
    let lruMediaKey = null
    let oldestAccessTime = Date.now()

    for (const [mediaKey, entry] of this.cache.entries()) {
      if (entry.accessTime < oldestAccessTime) {
        oldestAccessTime = entry.accessTime
        lruMediaKey = mediaKey
      }
    }

    if (lruMediaKey) {
      this.cache.delete(lruMediaKey)
      console.log(`🎵 📦 EVICTED (LRU): ${lruMediaKey}`)
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} - Cache stats
   */
  getStats() {
    const loading = this.loadingPromises.size
    const cached = this.cache.size
    
    return {
      cached,
      loading,
      maxSize: this.maxCacheSize,
      utilization: (cached / this.maxCacheSize) * 100
    }
  }

  /**
   * Get list of currently cached song IDs
   * @returns {Array<string>}
   */
  getCachedSongIds() {
    return Array.from(this.cache.keys())
  }

  /**
   * Check if a song is currently being loaded
   * @param {string} mediaKey
   * @returns {boolean}
   */
  isLoading(mediaKey) {
    return this.loadingPromises.has(mediaKey)
  }

  /**
   * Check if a song has audio files available
   * @param {Object} song - Song object
   * @returns {boolean}
   */
  hasAudioFiles(song) {
    return hasAnyPlayableMedia(song)
  }

  /**
   * Preload multiple songs in parallel
   * @param {Array<Object>} songs - Array of song objects
   * @param {AudioContext} audioContext
   * @returns {Promise<void>}
   */
  async preloadSongs(songs, audioContext) {
    if (!songs || songs.length === 0) return

    console.log(`🎵 📦 PRELOADING ${songs.length} songs`)
    
    const preloadPromises = songs
      .filter(song => {
        const mediaKey = getSongMediaKey(song)
        return song && mediaKey && !this.has(mediaKey)
      })
      .filter(song => this.hasAudioFiles(song)) // Only preload songs with audio files
      .map(song => 
        this.loadAndCache(song, audioContext).catch(error => {
          const mediaKey = getSongMediaKey(song)
          console.warn(`🎵 📦 Preload failed for ${song.title || mediaKey}:`, error.message)
          return null
        })
      )

    await Promise.allSettled(preloadPromises)
  }

  /**
   * Set event callbacks
   */
  setEventCallbacks({ onCacheHit, onCacheMiss, onCacheInvalidation }) {
    this.onCacheHit = onCacheHit
    this.onCacheMiss = onCacheMiss  
    this.onCacheInvalidation = onCacheInvalidation
  }
}

export default AudioCache
